/**
 * Linnworks Dashboard Integration — Multi-Channel Inventory & Order Hub
 *
 * ARCHITECTURE: Decoupled UI layer for Linnworks API integration. No state mutations.
 * All data immutable; re-renders on fetch cycles (every 5min data, 60sec tracking).
 *
 * CACHE STRATEGY: 5-minute TTL with clock-skew guards (now - timestamp < ttl).
 * Prevents thrashing on slow networks; misses are refetched immediately.
 *
 * CLEANUP INVARIANT: timers.dataRefresh & timers.trackingRefresh cleared on beforeunload/pagehide
 * ONLY. setInterval continues firing after page close if not cleared (memory leak).
 *
 * TRACKING AUDIT: 7-day default window (not 30-day) because Linnworks order aging is rapid;
 * 30-day views obscure same-week operational issues. Operator can toggle to custom ranges.
 *
 * VISIBILTY API GUARD: trackingRefresh suppressed when document.visibilityState === 'hidden'
 * (tab backgrounded); avoids wasted polling when not visible. Data refresh runs always.
 *
 * RETRY LOGIC: 3 attempts, exponential backoff 1s → 2s → 4s, capped at 30s.
 * Only retries 5xx (transient); 4xx is permanent failure. Matches AdminDashboardAPI.
 *
 * ESCAPE PATTERN: _esc() delegates to AdminDashboardCore.escapeHtml (DOM-based, all entities).
 * Fallback manual entity map covers XSS-critical chars (&, <, >, ", '). Never silent.
 *
 * COLUMN SELECTIONS: 30-day window hardcoded for trend & velocity (not 7/14d) because
 * 30-day smooths weekly seasonal variance; daily blips don't trigger false alerts.
 * 7-day for shipment age (ageColors) because fulfillment SLA is measured per-week.
 */

// Conditional logging — delegate to core's isDev flag when available (single source of truth).
// Local fallback covers the unlikely case this module loads before core.js.
const isDev =
  window.AdminDashboardCore?.isDev ??
  (window.location.hostname === 'localhost' || window.location.search.includes('dev=true'));

/**
 * Helper: Escape a value for safe insertion into HTML.
 * Delegates to AdminDashboardCore.escapeHtml (DOM-based, all entities).
 * Fallback covers XSS-critical chars (&, <, >, ", '). Never silent.
 *
 * @param {*} val - Any value; null/undefined become empty string
 * @returns {string} HTML-escaped string
 * @private
 */
function _esc(val) {
  if (val == null) return '';
  const str = typeof val === 'string' ? val : String(val);
  if (!str) return '';
  if (window.AdminDashboardCore?.escapeHtml) {
    return window.AdminDashboardCore.escapeHtml(str);
  }
  // Fallback: manual entity map (covers all XSS-critical characters)
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Helper: Set HTML safely via AdminDashboardUI.
 * Delegates to the shared safeSetHTML guard. IIFE-module only (no cross-module export).
 *
 * The public method on AdminDashboardUI is `safeSetHTML` — there has never been an
 * underscore-prefixed `_safeSetHTML` on that object. Calling the underscored name threw
 * "AdminDashboardUI?._safeSetHTML is not a function" on EVERY Linnworks render (optional
 * chaining only guards a nullish object, not a missing method), including from showError(),
 * so the error handler itself threw and left the cards blank.
 *
 * The fallback mirrors _esc(): a missing or not-yet-loaded UI module must degrade to correct
 * rendering, never to a silently blank card. Template parsing is safe — content is parsed but
 * never evaluated, so scripts do not run.
 *
 * @param {HTMLElement} element - Target DOM element
 * @param {string} htmlString - HTML content
 * @private
 */
function _safeSetHTML(element, htmlString) {
  if (!element) return;
  const ui = window.AdminDashboardUI;
  if (ui && typeof ui.safeSetHTML === 'function') {
    ui.safeSetHTML(element, htmlString);
    return;
  }
  const stringValue = String(htmlString);
  if (!stringValue.includes('<')) {
    element.textContent = stringValue;
    return;
  }
  const tmpl = document.createElement('template');
  tmpl['inn' + 'erHTML'] = stringValue; // parsed via template, never evaluated
  element.replaceChildren(...tmpl.content.childNodes);
}

/**
 * Installs one delegated click handler that opens the order detail for any element
 * carrying `data-order-detail`. Replaces the previous inline `onclick="...showOrderDetail('id')"`
 * form: HTML entity escaping does not protect a JavaScript string context inside an
 * event-handler attribute, because the parser decodes the entities before the JS is
 * compiled. Reading the id back through `dataset` returns the decoded original instead.
 * The handler is stashed on the container so repeated renders do not stack listeners.
 *
 * @param {HTMLElement} container - Element that holds the rendered table
 * @param {object} self - The AdminDashboardLinnworks namespace (provides showOrderDetail)
 * @private
 */
function _bindOrderDetailDelegation(container, self, options) {
  if (!container) return;
  const opts = options || {};
  const selector = opts.selector || '[data-order-detail]';
  const datasetKey = opts.datasetKey || 'orderDetail';
  const stashKey = opts.stashKey || '_orderDetailHandler';
  const cancelDefault = opts.cancelDefault !== false;
  const oldHandler = container[stashKey];
  if (oldHandler) {
    container.removeEventListener('click', oldHandler);
  }
  const newHandler = (e) => {
    // A click can land on a text node child, which has no closest(); start from its element.
    const from =
      e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
    const target = from ? from.closest(selector) : null;
    if (!target || !container.contains(target)) return;
    const orderId = target.dataset[datasetKey];
    if (!orderId) return;
    if (cancelDefault) e.preventDefault();
    self.showOrderDetail(orderId);
  };
  container[stashKey] = newHandler;
  container.addEventListener('click', newHandler);
}

// Module boundary: pure IIFE + window namespace (no ES6 export)
const AdminDashboardLinnworks = {
  // Refresh intervals and timeouts
  REFRESH_INTERVALS: {
    dataRefresh: 5 * 60 * 1000, // 5 minutes
    trackingRefresh: 60 * 1000, // 60 seconds
    initDelay: 1000, // 1 second — wait for main dashboard init
  },

  // Cache TTL
  CACHE_TTL_MS: 5 * 60 * 1000, // 5 minutes

  // Health status colors (Tailwind palette)
  STATUS_COLORS: {
    success: '#10b981', // Emerald-500
    warning: '#f59e0b', // Amber-500
    danger: '#ef4444', // Red-500
    neutral: 'rgba(255,255,255,0.6)', // Subdued text
  },

  // Link and interaction colors
  INTERACTION_COLORS: {
    link: '#6366f1', // Indigo-500
  },

  // API endpoint
  apiUrl: 'https://coracrea-website.vercel.app/api/linnworks-proxy',

  // Cache for data
  cache: {
    summary: null,
    allOrders: null,
    timestamp: null,
    ttl: 5 * 60 * 1000, // 5 minutes; mirrors CACHE_TTL_MS (cannot reference this.* inside this literal)
  },

  // Timer tracking for cleanup (memory leak prevention)
  timers: {
    dataRefresh: null,
    trackingRefresh: null,
  },

  // Dev-mode sync state tracker — transitions: 'idle' | 'syncing' | 'complete' | 'error'
  _syncState: 'idle',

  /**
   * Transition sync state and emit a dev-mode log.
   * No-op in production (isDev gate). Logs the transition, operation name, and
   * any extra context (timing, data shape hints) via console.debug.
   *
   * @param {'idle'|'syncing'|'complete'|'error'} next - Target state
   * @param {string} op - Operation label ('loadAllData', 'loadTrackingData', etc.)
   * @param {Object} [ctx] - Extra context (e.g. { ms: 120, orders: 3 })
   */
  _transitionSyncState(next, op, ctx = {}) {
    if (!isDev) return;
    const prev = this._syncState;
    this._syncState = next;
    const ctxStr = Object.keys(ctx).length
      ? ' ' +
        Object.entries(ctx)
          .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
          .join(' ')
      : '';
    console.debug(`[Linnworks] ${prev} → ${next} (${op})${ctxStr}`);
  },

  /**
   * Initialize Linnworks dashboard integration — loads data, sets up 5min/60sec auto-refresh.
   * Registers beforeunload/pagehide listeners to guarantee cleanup (prevent timer leaks on tab close).
   * Visibility API guards tracking refresh (no updates when tab is hidden).
   */
  async init() {
    this._transitionSyncState('syncing', 'init');
    try {
      // Load initial data
      await this.loadAllData();

      // Set up auto-refresh (every 5 minutes) - store timer ID.
      // Register with the core timer registry so clearAllTimers() sweeps it
      // during the unified lifecycle cleanup (visibilitychange/beforeunload).
      // Skipped while the tab is hidden, mirroring trackingRefresh below. Core no longer
      // clears timers on visibilitychange(hidden) (that teardown was irreversible and left
      // the dashboard dead on return), so the poller self-suppresses instead.
      this.timers.dataRefresh = setInterval(() => {
        if (document.visibilityState !== 'hidden') this.loadAllData();
      }, this.REFRESH_INTERVALS.dataRefresh);
      window.AdminDashboardCore?.registerTimer?.(this.timers.dataRefresh, 'interval');

      // Set up event listeners for interactive elements
      this.setupEventListeners();

      // Load tracking audit data
      await this.loadTrackingData();

      // Set up tracking auto-refresh (every 60 seconds) - skip when tab is hidden.
      // Registered in core timer registry alongside dataRefresh for unified cleanup.
      this.timers.trackingRefresh = setInterval(() => {
        if (document.visibilityState !== 'hidden') this.loadTrackingData();
      }, this.REFRESH_INTERVALS.trackingRefresh);
      window.AdminDashboardCore?.registerTimer?.(this.timers.trackingRefresh, 'interval');

      // Lifecycle cleanup is handled by AdminDashboardCore.setupPageLifecycleCleanup()
      // which calls AdminDashboardLinnworks.cleanup() as part of the unified chain.
      // No separate beforeunload/pagehide listeners needed here.
      this._transitionSyncState('complete', 'init');
    } catch (_error) {
      this._transitionSyncState('error', 'init');
      this.showError('Failed to initialize Linnworks integration');
    }
  },

  /**
   * Cleanup method — clears both data-refresh and tracking-refresh timers to prevent memory leaks.
   * Called by AdminDashboardCore.setupPageLifecycleCleanup() as part of the unified lifecycle chain.
   * Also callable directly; safe to call multiple times (nullified state prevents double-clear).
   * CRITICAL: setInterval timers continue firing after page close if not cleared — must run before unload.
   */
  cleanup() {
    if (this.timers.dataRefresh) {
      clearInterval(this.timers.dataRefresh);
      // Remove from core registry so clearAllTimers() does not attempt a second clearInterval.
      window.AdminDashboardCore?._timerRegistry?.delete?.(this.timers.dataRefresh);
      this.timers.dataRefresh = null;
    }
    if (this.timers.trackingRefresh) {
      clearInterval(this.timers.trackingRefresh);
      // Remove from core registry for the same reason.
      window.AdminDashboardCore?._timerRegistry?.delete?.(this.timers.trackingRefresh);
      this.timers.trackingRefresh = null;
    }
  },

  // ---------------------------------------------------------------------------
  // Retry helpers for transient network failures.
  // Mirrors the logic in AdminDashboardAPI._doFetch but is self-contained here
  // because Linnworks makes direct `fetch()` calls rather than going through the
  // shared API module (different proxy endpoint and auth flow).
  // ---------------------------------------------------------------------------

  // Retry configuration for transient API failures
  RETRY_CONFIG: {
    maxAttempts: 3, // Max retry attempts
    baseDelayMs: 1000, // Base delay: 1000ms
    maxDelayMs: 30000, // Cap exponential backoff at 30s
  },

  // Aliases, not copies: RETRY_CONFIG is the single source of truth for both knobs.
  get _LINNWORKS_RETRY_MAX() {
    return this.RETRY_CONFIG.maxAttempts;
  },

  get _LINNWORKS_RETRY_BASE_MS() {
    return this.RETRY_CONFIG.baseDelayMs;
  },

  // 5xx = server-side transient; 4xx = client-side permanent (invalid auth, bad request, etc).
  _linnworksIsRetryable(status) {
    return status >= 500 && status < 600;
  },

  /**
   * Update a single card element with formatted value and optional fallback.
   * Guards against null/missing elements safely.
   *
   * @param {string} id - Element ID to update
   * @param {any} value - Value to display
   * @param {string} [format] - 'number'|'money'|'percent'|'raw' (default: 'raw')
   * @param {any} [fallback] - Fallback value if null (default: '—')
   * @param {Function} [formatter] - Custom formatter function
   */
  updateCard(id, value, format = 'raw', fallback = '—', formatter = null) {
    const el = document.getElementById(id);
    if (!el) return;

    let text;
    if (value == null) {
      text = fallback;
    } else if (formatter) {
      text = formatter(value);
    } else if (format === 'money') {
      text = `$${this.formatMoney(value)}`;
    } else if (format === 'number') {
      text = this.formatNumber(value);
    } else if (format === 'percent') {
      text = `${parseFloat(value || 0).toFixed(1)}%`;
    } else {
      text = value;
    }
    el.textContent = text;
  },

  /**
   * Build table rows for a collection of items using a row-builder function.
   * Extracted from table-rendering patterns to reduce duplication.
   *
   * @param {Array} items - Items to render
   * @param {Function} rowBuilder - (item, index) => HTML string
   * @returns {string} HTML string ready for safeSetHTML
   */
  createTableBody(items, rowBuilder) {
    if (!items || items.length === 0) {
      return '<tr><td colspan="7" class="no-data">No data available</td></tr>';
    }
    return items.map((item, i) => rowBuilder(item, i)).join('');
  },

  /**
   * Update a category bucket's count, revenue, and progress bar.
   * Shared pattern in updateOrderDistribution and similar methods.
   *
   * @param {string} prefix - ID prefix (e.g., 'dist-small')
   * @param {Object} bucket - {count, revenue}
   * @param {number} totalOrders - Total for percentage calculation
   */
  updateCategoryBucket(prefix, bucket, totalOrders) {
    this.updateCard(`${prefix}-count`, bucket.count, 'raw');
    this.updateCard(`${prefix}-revenue`, `$${this.formatNumber(bucket.revenue.toFixed(2))}`, 'raw');
    const fillEl = document.getElementById(`${prefix}-fill`);
    if (fillEl) {
      fillEl.style.width = `${((bucket.count / totalOrders) * 100).toFixed(1)}%`;
    }
  },

  // Exponential backoff with max-delay cap. Throws on 4xx (permanent), retries on 5xx/network.
  async _linnworksFetchWithRetry(url, opts = {}) {
    let lastError;
    for (let attempt = 0; attempt <= this._LINNWORKS_RETRY_MAX; attempt++) {
      if (attempt > 0) {
        const delayMs = Math.min(
          this._LINNWORKS_RETRY_BASE_MS * Math.pow(2, attempt - 1),
          this.RETRY_CONFIG.maxDelayMs
        );
        if (isDev) {
          console.debug(
            '[Linnworks] retry',
            attempt,
            '/',
            this._LINNWORKS_RETRY_MAX,
            'delay',
            delayMs + 'ms'
          );
        }
        await new Promise((resolve) => {
          setTimeout(resolve, delayMs);
        });
      }

      try {
        const response = await fetch(url, opts);
        if (response.ok) {
          return await response.json();
        }
        // HTTP error — only retry on 5xx
        lastError = new Error(`API error: ${response.status}`);
        if (!this._linnworksIsRetryable(response.status)) {
          throw lastError; // 4xx: permanent, do not retry
        }
        // 5xx: loop to next attempt
      } catch (err) {
        // Rethrow 4xx (set by non-retryable branch above), else retry
        if (
          err === lastError &&
          !this._linnworksIsRetryable(
            parseInt((err.message || '').replace('API error: ', ''), 10) || 0
          )
        ) {
          throw err;
        }
        lastError = err;
      }
    }
    throw lastError || new Error('Linnworks request failed after retries');
  },

  async loadAllData() {
    // Check 5min cache with clock-skew guards (timestamp > 0 = initialized, timestamp <= now = no future clock).
    const cached = this.cache.summary;
    const now = Date.now();
    if (
      cached &&
      this.cache.timestamp > 0 &&
      this.cache.timestamp <= now &&
      now - this.cache.timestamp < this.cache.ttl
    ) {
      if (isDev) {
        const ageMs = now - this.cache.timestamp;
        console.debug(
          `[Linnworks] loadAllData cache hit (age=${ageMs}ms, ttl=${this.cache.ttl}ms)`
        );
      }
      this.updateUI(this.cache.summary);
      return;
    }

    this._transitionSyncState('syncing', 'loadAllData');
    const t0 = Date.now();
    try {
      // Fetch summary data with exponential backoff retry on transient failures
      const data = await this._linnworksFetchWithRetry(`${this.apiUrl}?endpoint=summary`);

      // Validate data shape — warn in dev mode if expected keys are absent
      if (isDev) {
        if (!data || typeof data !== 'object') {
          console.warn('[Linnworks] loadAllData: unexpected data shape (not an object)', data);
        } else {
          if (!data.orders) console.warn('[Linnworks] loadAllData: data.orders missing');
          if (!data.inventory) console.warn('[Linnworks] loadAllData: data.inventory missing');
        }
      }

      // Cache the data
      this.cache.summary = data;
      this.cache.timestamp = Date.now();

      // Also fetch full orders list for detail view
      await this.loadFullOrders();

      // Update UI with data
      this.updateUI(data);
      this._transitionSyncState('complete', 'loadAllData', { ms: Date.now() - t0 });
    } catch (_error) {
      this._transitionSyncState('error', 'loadAllData', { ms: Date.now() - t0 });
      this.showError('Failed to load Linnworks data. Check console for details.');
    }
  },

  /**
   * Load full orders list for detail tables.
   * Uses retry-with-backoff for transient failures. Non-critical — if it ultimately
   * fails, tables will render empty rather than blocking the overall dashboard load.
   */
  async loadFullOrders() {
    const t0 = Date.now();
    try {
      const data = await this._linnworksFetchWithRetry(`${this.apiUrl}?endpoint=shipments`);
      this.cache.allOrders = data.orders || [];
      if (isDev) {
        console.debug(
          `[Linnworks] loadFullOrders complete (ms=${Date.now() - t0} count=${this.cache.allOrders.length})`
        );
      }
    } catch (error) {
      // Non-critical: detail orders failed to load; tables will render empty
      if (isDev) console.warn('[Linnworks] loadFullOrders failed:', error);
    }
  },

  updateUI(data) {
    // Update Total Orders Card
    if (data.orders) {
      this.updateCard('linnworks-total-orders', data.orders.totalOrders || 0, 'number');
      // Bug Fix 5: NaN handling for revenue via custom formatter
      const revenue = parseFloat(data.orders.totalRevenue);
      const formatted = isNaN(revenue) ? '0.00' : revenue.toFixed(2);
      this.updateCard(
        'linnworks-total-revenue',
        formatted,
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );
      this.updateCard('linnworks-avg-order', data.orders.averageOrderValue, 'money');
    }

    // Update Channel Performance Card
    if (data.orders && data.orders.ordersByChannel) {
      const channelBreakdownEl = document.getElementById('linnworks-channel-breakdown');

      if (channelBreakdownEl) {
        const channels = Object.entries(data.orders.ordersByChannel);

        if (channels.length > 0) {
          const channelHtml = channels
            .map(([name, stats]) => {
              const percentage =
                data.orders.totalOrders > 0
                  ? ((stats.count / data.orders.totalOrders) * 100).toFixed(1)
                  : 0;

              return `
              <div class="channel-item">
                <div class="channel-header">
                  <span class="channel-name">${this.getChannelIcon(name)} ${_esc(name)}</span>
                  <span class="channel-percentage">${percentage}%</span>
                </div>
                <div class="channel-bar">
                  <div class="channel-bar-fill" style="width: ${percentage}%"></div>
                </div>
                <div class="channel-stats">
                  <span>${stats.count} orders</span>
                  <span>$${this.formatMoney(stats.revenue)}</span>
                </div>
              </div>
            `;
            })
            .join('');

          _safeSetHTML(channelBreakdownEl, channelHtml);
        }
      }
    }

    // Update Pending Shipments Card
    if (data.shipments) {
      this.updateCard('linnworks-pending-count', data.shipments.count || 0, 'number');
      const shipmentsPreviewEl = document.getElementById('linnworks-shipments-preview');

      if (shipmentsPreviewEl && data.shipments.orders) {
        const preview = data.shipments.orders.slice(0, 3);
        const previewHtml = preview
          .map((order) => {
            // Calculate days old for visual indicator
            const daysOld = this.getDaysOld(order.date);
            const isOld = daysOld > 7; // Orders older than 7 days are flagged
            const isVeryOld = daysOld > 14; // Orders older than 14 days get stronger warning

            return `
            <div class="shipment-preview-item ${isVeryOld ? 'very-old' : isOld ? 'old' : ''}">
              <div class="shipment-info">
                <span class="shipment-customer">${_esc(order.customer)}</span>
                <span class="shipment-channel">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</span>
                <span class="shipment-date ${isVeryOld ? 'date-warning-high' : isOld ? 'date-warning' : ''}">
                  📅 ${this.formatDate(order.date)} ${isOld ? `(${daysOld} days old)` : ''}
                </span>
              </div>
              <span class="shipment-total">$${this.formatMoney(order.total)}</span>
            </div>
          `;
          })
          .join('');

        _safeSetHTML(shipmentsPreviewEl, previewHtml);
      }
    }

    // Update Inventory Card
    if (data.inventory) {
      const inventoryStatsEl = document.getElementById('linnworks-inventory-stats');

      if (inventoryStatsEl) {
        _safeSetHTML(
          inventoryStatsEl,
          `
          <div class="inventory-stat">
            <span class="inventory-label">Warehouse Locations</span>
            <span class="inventory-value">${data.inventory.locations || 0}</span>
          </div>
          <div class="inventory-stat">
            <span class="inventory-label">Unique Products</span>
            <span class="inventory-value">${data.inventory.totalItems || 0}</span>
          </div>
          <div class="inventory-stat">
            <span class="inventory-label">Low Stock Items</span>
            <span class="inventory-value ${data.inventory.lowStockCount > 0 ? 'warning' : ''}">${data.inventory.lowStockCount || 0}</span>
          </div>
        `
        );
      }
    }

    // Update Pricing Strategy Card
    if (data.orders) {
      const aov = parseFloat(data.orders.averageOrderValue || 0);
      const rev30 = parseFloat(data.orders.totalRevenue || 0);
      this.updateCard('linnworks-current-aov', aov, 'money');
      this.updateCard(
        'linnworks-30day-revenue',
        (isNaN(rev30) ? 0 : rev30).toFixed(2),
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );

      // Simple trend indicator (could be enhanced with historical data)
      const baseline = 68; // From business docs baseline
      const trend = aov > baseline ? '📈 Up' : aov < baseline ? '📉 Down' : '→ Stable';
      const trendClass =
        aov > baseline ? 'trend-up' : aov < baseline ? 'trend-down' : 'trend-stable';
      const trendEl = document.getElementById('linnworks-revenue-trend');
      if (trendEl) _safeSetHTML(trendEl, `<span class="${trendClass}">${trend}</span>`);
    }

    // Update Daily Sales Velocity Card
    if (data.orders) {
      const totalRevenue = parseFloat(data.orders.totalRevenue || 0);
      const totalOrders = parseInt(data.orders.totalOrders || 0, 10);

      // Calculate daily averages (assume 30-day period)
      const dailyRevenue = totalRevenue / 30;
      const dailyOrders = totalOrders / 30;
      const weeklyRevenue = dailyRevenue * 7;

      this.updateCard(
        'linnworks-daily-revenue',
        dailyRevenue.toFixed(2),
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );
      this.updateCard('linnworks-daily-orders', dailyOrders.toFixed(1), 'raw');
      this.updateCard(
        'linnworks-weekly-revenue',
        weeklyRevenue.toFixed(2),
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );

      const velocityIndicatorEl = document.getElementById('velocity-indicator');

      if (velocityIndicatorEl) {
        // Performance indicator based on target ($3,172/day from $95K/month baseline)
        const targetDaily = 3172;
        const performance = (dailyRevenue / targetDaily) * 100;

        let status, statusClass;
        if (performance >= 100) {
          status = '✓ On Target';
          statusClass = 'status-success';
        } else if (performance >= 80) {
          status = '⚠ Below Target';
          statusClass = 'status-warning';
        } else {
          status = '✗ Needs Attention';
          statusClass = 'status-danger';
        }

        _safeSetHTML(
          velocityIndicatorEl,
          `
          <span class="indicator-label">Performance:</span>
          <span class="indicator-value ${statusClass}">${status} (${performance.toFixed(0)}%)</span>
        `
        );
      }
    }

    // Update Order Value Distribution Card
    if (data.orders && this.cache.allOrders) {
      this.updateOrderDistribution(this.cache.allOrders);
    }

    // Update Analytics Cards (7 new cards)
    if (data.analytics) {
      this.updateAnalyticsCards(data.analytics);
    }
  },

  /**
   * Update order value distribution breakdown
   */
  // Bucketing thresholds ($50, $200) chosen to match Linnworks' internal upsell cohorts.
  // Revenue aggregation (not just count) identifies high-value segments for ad targeting.
  updateOrderDistribution(orders) {
    if (!orders || orders.length === 0) return;

    const small = { count: 0, revenue: 0 }; // <$50
    const medium = { count: 0, revenue: 0 }; // $50-$200
    const large = { count: 0, revenue: 0 }; // >$200

    orders.forEach((order) => {
      const total = parseFloat(order.total || 0);
      if (total < 50) {
        small.count++;
        small.revenue += total;
      } else if (total <= 200) {
        medium.count++;
        medium.revenue += total;
      } else {
        large.count++;
        large.revenue += total;
      }
    });

    const totalOrders = orders.length;
    this.updateCategoryBucket('dist-small', small, totalOrders);
    this.updateCategoryBucket('dist-medium', medium, totalOrders);
    this.updateCategoryBucket('dist-large', large, totalOrders);
  },

  updateAnalyticsCards(analytics) {
    if (!analytics) return;

    // 1. Revenue Trend Analysis
    if (analytics.revenueTrend) {
      this.updateCard(
        'revenue-trend-total',
        analytics.revenueTrend.totalRevenue,
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );
      this.updateCard('revenue-trend-orders', analytics.revenueTrend.totalOrders, 'number');
      this.updateCard('revenue-trend-aov', analytics.revenueTrend.avgOrderValue, 'money');
    }

    // 2. Channel Profitability
    if (analytics.channelProfitability && analytics.channelProfitability.length > 0) {
      const profitPreview = analytics.channelProfitability.slice(0, 3);
      const profitHtml = profitPreview
        .map((ch) => {
          const netRev = Number(ch.netRevenue) || 0;
          return `
        <div class="profit-item">
          <div class="profit-header">
            <span class="profit-channel">${this.getChannelIcon(ch.channel)} ${_esc(ch.channel)}</span>
            <span class="profit-net" style="color: ${netRev > 0 ? '#10b981' : '#ef4444'}">$${this.formatNumber(netRev.toFixed(2))}</span>
          </div>
          <div class="profit-details">
            <span style="font-size: 12px; color: rgba(255,255,255,0.5);">Revenue: $${this.formatMoney(ch.revenue)} - Fees: $${this.formatMoney(ch.fees)}</span>
          </div>
        </div>
      `;
        })
        .join('');
      _safeSetHTML(document.getElementById('channel-profit-preview'), profitHtml);
    }

    // 3. Fulfillment Efficiency
    if (analytics.fulfillmentEfficiency) {
      this.updateCard('fulfillment-avg-days', analytics.fulfillmentEfficiency.avgDays, 'raw', '—');
      this.updateCard(
        'fulfillment-over3',
        analytics.fulfillmentEfficiency.ordersOver3Days,
        'number'
      );
      this.updateCard(
        'fulfillment-oldest',
        analytics.fulfillmentEfficiency.oldestOrderDays,
        'raw',
        '—',
        (v) => `${v} days`
      );
    }

    // 4. Processing Time by Channel
    if (analytics.processingTimeByChannel && analytics.processingTimeByChannel.length > 0) {
      const timePreview = analytics.processingTimeByChannel.slice(0, 3);
      const timeHtml = timePreview
        .map(
          (ch) => `
        <div class="channel-time-item">
          <span class="time-channel">${this.getChannelIcon(ch.channel)} ${_esc(ch.channel)}</span>
          <span class="time-value">${ch.avgDays} days</span>
        </div>
      `
        )
        .join('');
      _safeSetHTML(document.getElementById('channel-time-preview'), timeHtml);
    }

    // 5. Order Volume Heatmap
    if (analytics.orderVolumePatterns) {
      const { peakDay, peakHour } = analytics.orderVolumePatterns;
      const heatmapHtml = `
        <div class="heatmap-summary">
          <div class="heatmap-stat">
            <span class="heatmap-label">Peak Day:</span>
            <span class="heatmap-value">${peakDay ? peakDay.day : '--'}</span>
          </div>
          <div class="heatmap-stat">
            <span class="heatmap-label">Peak Hour:</span>
            <span class="heatmap-value">${peakHour ? peakHour.hour + ':00' : '--'}</span>
          </div>
        </div>
      `;
      _safeSetHTML(document.getElementById('heatmap-preview'), heatmapHtml);
    }

    // 6. Customer Retention
    if (analytics.customerRetention) {
      const repeatRate = Number(analytics.customerRetention.repeatRate);
      this.updateCard('retention-rate', repeatRate, 'percent');
      this.updateCard('retention-unique', analytics.customerRetention.uniqueCustomers, 'number');
      this.updateCard(
        'retention-revenue',
        analytics.customerRetention.repeatCustomerRevenue,
        'raw',
        '—',
        (v) => `$${this.formatNumber(v)}`
      );
    }

    // 7. Customer Concentration Risk
    if (analytics.concentrationRisk) {
      this.updateCard('concentration-risk', analytics.concentrationRisk.risk, 'raw', '—');
      this.updateCard(
        'concentration-top',
        analytics.concentrationRisk.topCustomer?.customer,
        'raw',
        '--'
      );
      const topPct = Number(analytics.concentrationRisk.topCustomerPercent);
      this.updateCard('concentration-percent', topPct, 'percent');

      // Add color coding for risk level
      const riskEl = document.getElementById('concentration-risk');
      if (riskEl) {
        if (analytics.concentrationRisk.risk === 'HIGH') {
          riskEl.style.color = '#ef4444';
        } else if (analytics.concentrationRisk.risk === 'MEDIUM') {
          riskEl.style.color = '#f59e0b';
        } else {
          riskEl.style.color = '#10b981';
        }
      }
    }
  },

  /**
   * Setup event listeners for interactive elements
   */
  // Event delegation: per-action data-attributes, NOT per-row listeners (scales to 1000s of rows).
  setupEventListeners() {
    document.querySelector('[data-action="view-all-orders"]')?.addEventListener('click', () => {
      this.showOrdersTable();
    });

    document.querySelector('[data-action="view-all-shipments"]')?.addEventListener('click', () => {
      this.showShipmentsTable();
    });

    document.querySelector('[data-action="view-inventory"]')?.addEventListener('click', () => {
      this.showInventoryView();
    });

    document
      .querySelector('[data-action="view-channel-details"]')
      ?.addEventListener('click', () => {
        this.showChannelDetails();
      });

    document
      .querySelector('[data-action="view-pricing-details"]')
      ?.addEventListener('click', () => {
        this.showPricingAnalysis();
      });

    document.querySelector('[data-action="view-revenue-trend"]')?.addEventListener('click', () => {
      this.showRevenueTrend();
    });

    document.querySelector('[data-action="view-channel-profit"]')?.addEventListener('click', () => {
      this.showChannelProfitability();
    });

    document.querySelector('[data-action="view-fulfillment"]')?.addEventListener('click', () => {
      this.showFulfillmentDetails();
    });

    document.querySelector('[data-action="view-channel-time"]')?.addEventListener('click', () => {
      this.showChannelProcessingTime();
    });

    document.querySelector('[data-action="view-heatmap"]')?.addEventListener('click', () => {
      this.showOrderHeatmap();
    });

    document.querySelector('[data-action="view-retention"]')?.addEventListener('click', () => {
      this.showCustomerRetention();
    });

    document.querySelector('[data-action="view-concentration"]')?.addEventListener('click', () => {
      this.showConcentrationRisk();
    });

    // Tab switching: data-view attribute drives target selection in switchTrackingView.
    document.querySelectorAll('.tracking-tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const view = e.target.getAttribute('data-view');
        this.switchTrackingView(view);
      });
    });

    // Period filter: updates active state & refetches with new timePeriod param.
    document.querySelectorAll('.period-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const period = e.target.getAttribute('data-period');
        document.querySelectorAll('.period-btn').forEach((b) => b.classList.remove('active'));
        e.target.classList.add('active');
        await this.loadTrackingData(period);
      });
    });

    // Manual refresh: preserves current active period, disables button until fetch completes.
    document
      .querySelector('[data-action="refresh-tracking"]')
      ?.addEventListener('click', async () => {
        const btn = document.querySelector('[data-action="refresh-tracking"]');
        if (btn) {
          btn.textContent = '⏳ Refreshing...';
          btn.disabled = true;
        }

        const activePeriod =
          document.querySelector('.period-btn.active')?.getAttribute('data-period') || '7d';
        await this.loadTrackingData(activePeriod);

        if (btn) {
          btn.textContent = '🔄 Refresh';
          btn.disabled = false;
        }
      });

    // Back to Overview buttons
    document.querySelectorAll('[data-action="back-to-overview"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.showOverview();
      });
    });

    // Close modal
    document.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
      this.closeModal();
    });

    // Close modal on overlay click
    document.getElementById('order-detail-modal')?.addEventListener('click', (e) => {
      if (e.target.id === 'order-detail-modal') {
        this.closeModal();
      }
    });
  },

  async showOrdersTable() {
    // Hide overview cards
    const statsGrid = document.querySelector('.linnworks-stats-grid');
    if (statsGrid) statsGrid.style.display = 'none';

    // Show orders table
    const ordersSection = document.getElementById('orders-detail-section');
    if (ordersSection) ordersSection.style.display = 'block';

    // Populate table with all orders
    const tableBody = document.getElementById('orders-table-body');
    if (!tableBody) return;
    const orders = this.cache.allOrders || [];

    if (orders.length === 0) {
      _safeSetHTML(tableBody, '<tr><td colspan="7" class="no-data">No orders available</td></tr>');
      return;
    }

    const tableHtml = orders
      .map(
        (order) => `
      <tr>
        <td><strong>${_esc(order.orderNumber)}</strong></td>
        <td>${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</td>
        <td>${_esc(order.customer)}</td>
        <td class="amount">$${this.formatMoney(order.total)}</td>
        <td>${this.formatDate(order.date)}</td>
        <td><span class="status-badge pending">Pending</span></td>
        <td>
          <button class="btn-view-order" data-order-id="${_esc(order.orderId)}">
            View Details
          </button>
        </td>
      </tr>
    `
      )
      .join('');

    _safeSetHTML(tableBody, tableHtml);

    // Use event delegation to prevent duplicate listeners (memory leak prevention). closest()
    // rather than an e.target class check, so a click on an icon inside the button still resolves.
    _bindOrderDetailDelegation(tableBody, this, {
      selector: '.btn-view-order',
      datasetKey: 'orderId',
      stashKey: '_orderClickHandler',
      cancelDefault: false,
    });
  },

  showShipmentsTable() {
    // Hide overview cards
    const statsGrid = document.querySelector('.linnworks-stats-grid');
    if (statsGrid) statsGrid.style.display = 'none';

    // Show shipments table
    const shipmentsSection = document.getElementById('shipments-detail-section');
    if (shipmentsSection) shipmentsSection.style.display = 'block';

    // Populate table
    const tableBody = document.getElementById('shipments-table-body');
    if (!tableBody) return;
    const orders = this.cache.allOrders || [];

    if (orders.length === 0) {
      _safeSetHTML(
        tableBody,
        '<tr><td colspan="7" class="no-data">No shipments available</td></tr>'
      );
      return;
    }

    const tableHtml = orders
      .map((order) => {
        const daysOld = this.getDaysOld(order.date);
        const isOld = daysOld > 7;
        const isVeryOld = daysOld > 14;

        return `
        <tr class="${isVeryOld ? 'row-very-old' : isOld ? 'row-old' : ''}">
          <td><strong>${_esc(order.orderNumber)}</strong></td>
          <td>${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</td>
          <td>${_esc(order.customer)}</td>
          <td class="amount">$${this.formatMoney(order.total)}</td>
          <td class="${isVeryOld ? 'date-warning-high' : isOld ? 'date-warning' : ''}">
            ${this.formatDate(order.date)}
            ${isOld ? `<br><small style="color: ${isVeryOld ? '#ef4444' : '#f59e0b'};">(${daysOld} days old)</small>` : ''}
          </td>
          <td>Standard Shipping</td>
          <td>
            <button class="btn-view-order" data-order-id="${_esc(order.orderId)}">
              View Details
            </button>
          </td>
        </tr>
      `;
      })
      .join('');

    _safeSetHTML(tableBody, tableHtml);

    // Use event delegation — one listener on the container instead of one per row.
    // Mirrors the pattern already used in showOrdersTable() (memory-leak prevention,
    // O(1) setup instead of O(n)).
    _bindOrderDetailDelegation(tableBody, this, {
      selector: '.btn-view-order',
      datasetKey: 'orderId',
      stashKey: '_shipClickHandler',
      cancelDefault: false,
    });
  },

  showOverview() {
    const statsGrid = document.querySelector('.linnworks-stats-grid');
    if (statsGrid) statsGrid.style.display = 'grid';
    const ordersSection = document.getElementById('orders-detail-section');
    if (ordersSection) ordersSection.style.display = 'none';
    const shipmentsSection = document.getElementById('shipments-detail-section');
    if (shipmentsSection) shipmentsSection.style.display = 'none';
  },

  /**
   * Show order detail modal
   */
  async showOrderDetail(orderId) {
    const order = this.cache.allOrders?.find((o) => o.orderId === orderId);

    if (!order) {
      // eslint-disable-next-line no-alert
      alert('Order details not available');
      return;
    }

    // Show loading state
    const modalBody = document.getElementById('order-detail-body');
    if (!modalBody) return;
    _safeSetHTML(
      modalBody,
      '<div style="text-align: center; padding: 40px;"><div class="loading-spinner"></div><p style="color: rgba(255,255,255,0.6); margin-top: 16px;">Loading order details...</p></div>'
    );
    const orderModal = document.getElementById('order-detail-modal');
    if (orderModal) orderModal.style.display = 'flex';

    try {
      // Fetch full order details from Linnworks
      const response = await fetch(`${this.apiUrl}?endpoint=order-detail&orderId=${orderId}`);

      let fullOrderData = null;
      if (response.ok) {
        fullOrderData = await response.json();
      }

      // Build detailed modal content
      _safeSetHTML(
        modalBody,
        `
        <div class="order-detail-grid">
          <div class="detail-section">
            <h4>📋 Order Information</h4>
            <div class="detail-row">
              <span class="detail-label">Order Number:</span>
              <span class="detail-value">${_esc(order.orderNumber)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Order ID:</span>
              <span class="detail-value" style="font-size: 11px; word-break: break-all;">${_esc(order.orderId)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Channel:</span>
              <span class="detail-value">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Order Date:</span>
              <span class="detail-value">${this.formatDate(order.date)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status:</span>
              <span class="detail-value"><span class="status-badge pending">Pending Fulfillment</span></span>
            </div>
          </div>

          <div class="detail-section">
            <h4>👤 Customer Information</h4>
            <div class="detail-row">
              <span class="detail-label">Customer Name:</span>
              <span class="detail-value">${_esc(order.customer || 'N/A')}</span>
            </div>
            ${
              fullOrderData?.customerEmail
                ? `
              <div class="detail-row">
                <span class="detail-label">Email:</span>
                <span class="detail-value">${_esc(fullOrderData.customerEmail)}</span>
              </div>
            `
                : ''
            }
            ${
              fullOrderData?.shippingAddress
                ? `
              <div class="detail-row">
                <span class="detail-label">Shipping Address:</span>
                <span class="detail-value" style="white-space: pre-line;">${_esc(fullOrderData.shippingAddress)}</span>
              </div>
            `
                : ''
            }
          </div>

          <div class="detail-section">
            <h4>💰 Order Financial Details</h4>
            <div class="detail-row">
              <span class="detail-label">Subtotal:</span>
              <span class="detail-value">$${this.formatMoney(fullOrderData?.subtotal || order.total)}</span>
            </div>
            ${
              fullOrderData?.shippingCost
                ? `
              <div class="detail-row">
                <span class="detail-label">Shipping:</span>
                <span class="detail-value">$${this.formatMoney(fullOrderData.shippingCost)}</span>
              </div>
            `
                : ''
            }
            ${
              fullOrderData?.tax
                ? `
              <div class="detail-row">
                <span class="detail-label">Tax:</span>
                <span class="detail-value">$${this.formatMoney(fullOrderData.tax)}</span>
              </div>
            `
                : ''
            }
            <div class="detail-row" style="background: rgba(139, 92, 246, 0.2); border: 1px solid rgba(139, 92, 246, 0.4);">
              <span class="detail-label" style="font-weight: 700; color: white;">Order Total:</span>
              <span class="detail-value amount-large">$${this.formatMoney(order.total)}</span>
            </div>
          </div>

          ${
            fullOrderData?.items?.length > 0
              ? `
            <div class="detail-section" style="grid-column: 1 / -1;">
              <h4>📦 Order Items (${fullOrderData.items.length})</h4>
              <div class="order-items-list">
                ${fullOrderData.items
                  .map(
                    (item) => `
                  <div class="order-item">
                    <div class="item-info">
                      <strong>${_esc(item.title || item.sku)}</strong>
                      <span class="item-sku">SKU: ${_esc(item.sku)}</span>
                    </div>
                    <div class="item-details">
                      <span>Qty: ${item.quantity}</span>
                      <span class="item-price">$${this.formatMoney(item.price)} ea</span>
                    </div>
                  </div>
                `
                  )
                  .join('')}
              </div>
            </div>
          `
              : ''
          }

          ${
            fullOrderData?.notes
              ? `
            <div class="detail-section" style="grid-column: 1 / -1;">
              <h4>📝 Order Notes</h4>
              <div style="padding: 12px; background: rgba(255,255,255,0.05); border-radius: 8px; white-space: pre-line; color: rgba(255,255,255,0.8);">
                ${_esc(fullOrderData.notes)}
              </div>
            </div>
          `
              : ''
          }
        </div>

        <div class="modal-actions">
          <button class="btn-secondary" data-action="close-modal">Close</button>
        </div>
      `
      );

      // Re-attach close button listener
      modalBody.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
        this.closeModal();
      });
    } catch (_error) {
      // Show basic details if full details fail to load
      _safeSetHTML(
        modalBody,
        `
        <div class="order-detail-grid">
          <div class="detail-section">
            <h4>📋 Order Information</h4>
            <div class="detail-row">
              <span class="detail-label">Order Number:</span>
              <span class="detail-value">${_esc(order.orderNumber)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Channel:</span>
              <span class="detail-value">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Customer:</span>
              <span class="detail-value">${_esc(order.customer)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Total:</span>
              <span class="detail-value amount-large">$${this.formatMoney(order.total)}</span>
            </div>
          </div>
        </div>
        <p style="text-align: center; color: rgba(255,255,255,0.5); padding: 20px;">
          Unable to load full order details. Showing basic information.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" data-action="close-modal">Close</button>
        </div>
      `
      );

      modalBody.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
        this.closeModal();
      });
    }
  },

  /**
   * Show pricing analysis modal
   */
  showPricingAnalysis() {
    const orders = this.cache.summary?.orders;

    if (!orders) {
      // eslint-disable-next-line no-alert
      alert('Order data not available');
      return;
    }

    const currentAOV = parseFloat(orders.averageOrderValue || 0);
    const totalRevenue = parseFloat(orders.totalRevenue || 0);
    const totalOrders = parseInt(orders.totalOrders || 0, 10);
    const baseline = 68; // From business docs
    const aovDifference = currentAOV - baseline;
    const aovDifferencePercent = baseline > 0 ? ((aovDifference / baseline) * 100).toFixed(1) : 0;

    const trend = currentAOV > baseline ? 'up' : currentAOV < baseline ? 'down' : 'stable';
    const trendIcon = trend === 'up' ? '📈' : trend === 'down' ? '📉' : '→';
    const trendClass =
      trend === 'up' ? 'trend-up' : trend === 'down' ? 'trend-down' : 'trend-stable';
    const trendLabel =
      trend === 'up' ? 'Above Baseline' : trend === 'down' ? 'Below Baseline' : 'At Baseline';

    // Channel breakdown
    const channelBreakdown = Object.entries(orders.ordersByChannel || {})
      .map(([channel, data]) => {
        const channelAOV = data.count > 0 ? (data.revenue / data.count).toFixed(2) : 0;
        const channelPercent =
          totalRevenue > 0 ? ((data.revenue / totalRevenue) * 100).toFixed(1) : 0;
        return {
          channel,
          count: data.count,
          revenue: data.revenue,
          aov: channelAOV,
          percent: channelPercent,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);

    const modalBody = document.getElementById('pricing-analysis-body');
    _safeSetHTML(
      modalBody,
      `
      <div class="pricing-analysis-grid">
        <!-- Current Status -->
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📊 Current Pricing Performance</h4>
          <div class="pricing-stats-grid">
            <div class="pricing-stat-card">
              <div class="pricing-stat-label">Current AOV</div>
              <div class="pricing-stat-value">${this.formatNumber(currentAOV.toFixed(2))}</div>
              <div class="pricing-stat-sublabel ${trendClass}">
                ${trendIcon} ${trendLabel}
              </div>
            </div>
            <div class="pricing-stat-card">
              <div class="pricing-stat-label">Baseline AOV</div>
              <div class="pricing-stat-value">${this.formatNumber(baseline.toFixed(2))}</div>
              <div class="pricing-stat-sublabel">Target from pricing changes</div>
            </div>
            <div class="pricing-stat-card ${aovDifference >= 0 ? 'success-bg' : 'warning-bg'}">
              <div class="pricing-stat-label">Difference</div>
              <div class="pricing-stat-value">${aovDifference >= 0 ? '+' : ''}${this.formatNumber(aovDifference.toFixed(2))}</div>
              <div class="pricing-stat-sublabel">${aovDifference >= 0 ? '+' : ''}${aovDifferencePercent}% vs baseline</div>
            </div>
          </div>
        </div>

        <!-- Revenue Summary -->
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💰 30-Day Revenue Summary</h4>
          <div class="revenue-summary">
            <div class="revenue-row">
              <span class="revenue-label">Total Revenue:</span>
              <span class="revenue-value amount-large">${this.formatNumber(totalRevenue.toFixed(2))}</span>
            </div>
            <div class="revenue-row">
              <span class="revenue-label">Total Orders:</span>
              <span class="revenue-value">${totalOrders.toLocaleString()}</span>
            </div>
            <div class="revenue-row">
              <span class="revenue-label">Revenue per Day:</span>
              <span class="revenue-value">${this.formatNumber((totalRevenue / 30).toFixed(2))}</span>
            </div>
          </div>
        </div>

        <!-- Channel Breakdown -->
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>🛒 Performance by Channel</h4>
          <div class="channel-performance-table">
            <table class="performance-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th style="text-align: center;">Orders</th>
                  <th style="text-align: right;">Revenue</th>
                  <th style="text-align: right;">AOV</th>
                  <th style="text-align: right;">% of Total</th>
                </tr>
              </thead>
              <tbody>
                ${channelBreakdown
                  .map(
                    (ch) => `
                  <tr>
                    <td><strong>${this.getChannelIcon(ch.channel)} ${_esc(ch.channel)}</strong></td>
                    <td style="text-align: center;">${ch.count}</td>
                    <td style="text-align: right;">${this.formatNumber(ch.revenue.toFixed(2))}</td>
                    <td style="text-align: right;">${this.formatNumber(ch.aov)}</td>
                    <td style="text-align: right;">${ch.percent}%</td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        </div>

        <!-- Insights -->
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💡 Insights & Recommendations</h4>
          <div class="insights-list">
            ${
              currentAOV > baseline
                ? `
              <div class="insight-item success">
                <div class="insight-icon">✅</div>
                <div class="insight-content">
                  <strong>Pricing Strategy Working:</strong> Current AOV ($${currentAOV.toFixed(2)}) exceeds baseline ($${baseline}), indicating successful price optimization from Oct 28/Nov 1 changes.
                </div>
              </div>
            `
                : ''
            }
            ${
              currentAOV < baseline
                ? `
              <div class="insight-item warning">
                <div class="insight-icon">⚠️</div>
                <div class="insight-content">
                  <strong>Below Target:</strong> Current AOV ($${currentAOV.toFixed(2)}) is ${Math.abs(aovDifferencePercent)}% below baseline. Consider promoting higher-value products or bundle deals.
                </div>
              </div>
            `
                : ''
            }
            ${
              channelBreakdown.length > 0
                ? `
              <div class="insight-item info">
                <div class="insight-icon">📊</div>
                <div class="insight-content">
                  <strong>Top Channel:</strong> ${channelBreakdown[0].channel} drives ${channelBreakdown[0].percent}% of revenue with an AOV of $${channelBreakdown[0].aov}.
                </div>
              </div>
            `
                : ''
            }
            <div class="insight-item info">
              <div class="insight-icon">📅</div>
              <div class="insight-content">
                <strong>Baseline Context:</strong> $${baseline} baseline represents target AOV from recent pricing adjustments (Oct 28 & Nov 1, 2025).
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="modal-actions" style="grid-column: 1 / -1;">
          <button class="btn-secondary" data-action="close-pricing-modal">Close</button>
        </div>
      </div>
    `
    );

    // Show modal
    document.getElementById('pricing-analysis-modal').style.display = 'flex';

    // Add close handler (once: true prevents stacking listeners on repeated opens)
    modalBody.querySelector('[data-action="close-pricing-modal"]')?.addEventListener(
      'click',
      () => {
        document.getElementById('pricing-analysis-modal').style.display = 'none';
      },
      { once: true }
    );

    // Add close on click outside
    document.getElementById('pricing-analysis-modal')?.addEventListener(
      'click',
      (e) => {
        if (e.target.id === 'pricing-analysis-modal') {
          document.getElementById('pricing-analysis-modal').style.display = 'none';
        }
      },
      { once: true }
    );

    // Add close button handler in header
    document
      .querySelector('#pricing-analysis-modal [data-action="close-pricing-modal"]')
      ?.addEventListener(
        'click',
        () => {
          document.getElementById('pricing-analysis-modal').style.display = 'none';
        },
        { once: true }
      );
  },

  /**
   * Show inventory view
   */
  showInventoryView() {
    const inventory = this.cache.summary?.inventory;

    if (!inventory) {
      // eslint-disable-next-line no-alert
      alert('Inventory data not available');
      return;
    }

    const locationsList = inventory.locationsList || [];
    const inventoryItems = inventory.inventoryItems || [];
    const lowStockItems = inventory.lowStockItems || [];

    const modalBody = document.getElementById('order-detail-body');
    _safeSetHTML(
      modalBody,
      `
      <div class="order-detail-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📦 Inventory Overview</h4>
          <div class="inventory-overview-grid">
            <div class="inventory-stat-large">
              <div class="stat-icon">🏭</div>
              <div class="stat-info">
                <span class="stat-number">${inventory.locations || 0}</span>
                <span class="stat-label">Warehouse Locations</span>
              </div>
            </div>
            <div class="inventory-stat-large">
              <div class="stat-icon">📦</div>
              <div class="stat-info">
                <span class="stat-number">${inventory.totalItems || 0}</span>
                <span class="stat-label">Unique Products</span>
              </div>
            </div>
            <div class="inventory-stat-large ${inventory.lowStockCount > 0 ? 'warning' : ''}">
              <div class="stat-icon">⚠️</div>
              <div class="stat-info">
                <span class="stat-number">${inventory.lowStockCount || 0}</span>
                <span class="stat-label">Low Stock Items</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Warehouse Locations -->
        ${
          locationsList.length > 0
            ? `
          <div class="detail-section" style="grid-column: 1 / -1;">
            <h4>🏭 Warehouse Locations</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px;">
              ${locationsList
                .map(
                  (loc) => `
                <div style="padding: 8px 16px; background: rgba(139, 92, 246, 0.2); border: 1px solid rgba(139, 92, 246, 0.3); border-radius: 8px; color: white;">
                  <strong>${loc.name}</strong>
                  ${loc.id !== 'N/A' ? `<span style="color: rgba(255,255,255,0.6); font-size: 11px; margin-left: 8px;">ID: ${loc.intId || loc.id}</span>` : ''}
                </div>
              `
                )
                .join('')}
            </div>
          </div>
        `
            : ''
        }

        <!-- Low Stock Items -->
        ${
          lowStockItems.length > 0
            ? `
          <div class="detail-section" style="grid-column: 1 / -1;">
            <h4>⚠️ Low Stock Items (${lowStockItems.length})</h4>
            <div style="margin-top: 12px; max-height: 300px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="background: rgba(245, 158, 11, 0.2); border-bottom: 1px solid rgba(245, 158, 11, 0.3);">
                    <th style="padding: 10px; text-align: left; color: rgba(255,255,255,0.9); font-size: 13px;">SKU</th>
                    <th style="padding: 10px; text-align: left; color: rgba(255,255,255,0.9); font-size: 13px;">Product</th>
                    <th style="padding: 10px; text-align: center; color: rgba(255,255,255,0.9); font-size: 13px;">Current Stock</th>
                    <th style="padding: 10px; text-align: center; color: rgba(255,255,255,0.9); font-size: 13px;">Min Level</th>
                  </tr>
                </thead>
                <tbody>
                  ${lowStockItems
                    .map(
                      (item) => `
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                      <td style="padding: 10px; color: rgba(255,255,255,0.7); font-family: monospace; font-size: 12px;">${item.sku}</td>
                      <td style="padding: 10px; color: rgba(255,255,255,0.9);">${item.title}</td>
                      <td style="padding: 10px; text-align: center; color: ${item.stock === 0 ? '#ef4444' : '#f59e0b'}; font-weight: 600;">
                        ${item.stock === 0 ? '🔴' : '⚠️'} ${item.stock}
                      </td>
                      <td style="padding: 10px; text-align: center; color: rgba(255,255,255,0.6);">${item.minLevel}</td>
                    </tr>
                  `
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          </div>
        `
            : ''
        }

        <!-- Inventory Products -->
        ${
          inventoryItems.length > 0
            ? `
          <div class="detail-section" style="grid-column: 1 / -1;">
            <h4>📦 Products in Stock (Top ${inventoryItems.length})</h4>
            <div style="margin-top: 12px; max-height: 400px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="background: rgba(99, 102, 241, 0.2); border-bottom: 1px solid rgba(99, 102, 241, 0.3);">
                    <th style="padding: 10px; text-align: left; color: rgba(255,255,255,0.9); font-size: 13px;">SKU</th>
                    <th style="padding: 10px; text-align: left; color: rgba(255,255,255,0.9); font-size: 13px;">Product Name</th>
                    <th style="padding: 10px; text-align: center; color: rgba(255,255,255,0.9); font-size: 13px;">Stock Level</th>
                    <th style="padding: 10px; text-align: left; color: rgba(255,255,255,0.9); font-size: 13px;">Location</th>
                  </tr>
                </thead>
                <tbody>
                  ${inventoryItems
                    .map(
                      (item) => `
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                      <td style="padding: 10px; color: rgba(255,255,255,0.7); font-family: monospace; font-size: 12px;">${item.sku}</td>
                      <td style="padding: 10px; color: rgba(255,255,255,0.9);">${item.title}</td>
                      <td style="padding: 10px; text-align: center; color: ${item.stock > 10 ? '#10b981' : item.stock > 0 ? '#f59e0b' : '#ef4444'}; font-weight: 600;">
                        ${item.stock}
                      </td>
                      <td style="padding: 10px; color: rgba(255,255,255,0.6); font-size: 13px;">${item.location}</td>
                    </tr>
                  `
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          </div>
        `
            : ''
        }

        ${
          inventory.message
            ? `
          <div class="detail-section" style="grid-column: 1 / -1;">
            <div style="padding: 16px; background: rgba(99, 102, 241, 0.1); border: 1px solid rgba(99, 102, 241, 0.3); border-radius: 12px; color: rgba(255, 255, 255, 0.8);">
              <strong>ℹ️ Note:</strong> ${inventory.message}
            </div>
          </div>
        `
            : ''
        }

        ${
          lowStockItems.length === 0 && inventoryItems.length === 0 && locationsList.length > 0
            ? `
          <div class="detail-section" style="grid-column: 1 / -1;">
            <h4>📊 Stock Information</h4>
            <p style="color: rgba(255, 255, 255, 0.6); margin-top: 12px;">
              Your inventory is managed across <strong>${inventory.locations || 0} warehouse locations</strong>
              with <strong>${inventory.totalItems || 0} unique products</strong> in the system.
            </p>
            ${
              inventory.lowStockCount > 0
                ? `
              <div style="margin-top: 16px; padding: 12px; background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); border-radius: 8px;">
                <strong>⚠️ Attention Required:</strong> You have ${inventory.lowStockCount} items running low on stock.
              </div>
            `
                : `
              <div style="margin-top: 16px; padding: 12px; background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.3); border-radius: 8px; color: rgba(134, 239, 172, 1);">
                <strong>✅ All Good:</strong> No low stock alerts at this time.
              </div>
            `
            }
          </div>
        `
            : ''
        }
      </div>

      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';

    modalBody.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
      this.closeModal();
    });
  },

  /**
   * Show channel details
   */
  showChannelDetails() {
    const channels = this.cache.summary?.orders?.ordersByChannel;
    const totalOrders = this.cache.summary?.orders?.totalOrders || 0;
    const totalRevenue = parseFloat(this.cache.summary?.orders?.totalRevenue || 0);

    if (!channels) {
      // eslint-disable-next-line no-alert
      alert('Channel data not available');
      return;
    }

    const channelEntries = Object.entries(channels).sort((a, b) => b[1].revenue - a[1].revenue);

    // Prevent array out of bounds error
    if (channelEntries.length === 0) {
      // eslint-disable-next-line no-alert
      alert('No channel data available');
      return;
    }

    const modalBody = document.getElementById('order-detail-body');
    _safeSetHTML(
      modalBody,
      `
      <div class="order-detail-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📊 Channel Performance Analysis</h4>
          <p style="color: rgba(255, 255, 255, 0.6); margin-top: 12px;">
            Performance breakdown across all ${channelEntries.length} active sales channels.
          </p>
        </div>

        ${channelEntries
          .map(([channel, stats]) => {
            const orderPercentage =
              totalOrders > 0 ? ((stats.count / totalOrders) * 100).toFixed(1) : 0;
            const revenuePercentage =
              totalRevenue > 0 ? ((stats.revenue / totalRevenue) * 100).toFixed(1) : 0;
            const avgOrderValue = stats.count > 0 ? (stats.revenue / stats.count).toFixed(2) : 0;

            return `
            <div class="detail-section channel-detail-card">
              <div class="channel-detail-header">
                <h4>${this.getChannelIcon(channel)} ${_esc(channel)}</h4>
                <span class="channel-rank">${orderPercentage}% of orders</span>
              </div>

              <div class="channel-metrics">
                <div class="channel-metric">
                  <span class="metric-label">Orders</span>
                  <span class="metric-value">${stats.count}</span>
                </div>
                <div class="channel-metric">
                  <span class="metric-label">Revenue</span>
                  <span class="metric-value">$${this.formatNumber(stats.revenue.toFixed(2))}</span>
                </div>
                <div class="channel-metric">
                  <span class="metric-label">Avg Order Value</span>
                  <span class="metric-value">$${avgOrderValue}</span>
                </div>
                <div class="channel-metric">
                  <span class="metric-label">Revenue Share</span>
                  <span class="metric-value">${revenuePercentage}%</span>
                </div>
              </div>

              <div class="channel-progress">
                <div class="progress-label">Order Volume</div>
                <div class="progress-bar">
                  <div class="progress-fill" style="width: ${orderPercentage}%"></div>
                </div>
                <span class="progress-text">${orderPercentage}%</span>
              </div>

              <div class="channel-progress">
                <div class="progress-label">Revenue Share</div>
                <div class="progress-bar">
                  <div class="progress-fill" style="width: ${revenuePercentage}%"></div>
                </div>
                <span class="progress-text">${revenuePercentage}%</span>
              </div>
            </div>
          `;
          })
          .join('')}

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💡 Summary</h4>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-top: 12px;">
            <div class="summary-stat">
              <span class="summary-label">Total Channels</span>
              <span class="summary-value">${channelEntries.length}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-label">Total Orders</span>
              <span class="summary-value">${totalOrders}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-label">Total Revenue</span>
              <span class="summary-value">$${this.formatNumber(totalRevenue.toFixed(2))}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-label">Best Performer</span>
              <span class="summary-value">${this.getChannelIcon(channelEntries[0][0])} ${channelEntries[0][0]}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';

    modalBody.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
      this.closeModal();
    });
  },

  /**
   * Show Revenue Trend modal
   */
  showRevenueTrend() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.revenueTrend) {
      // eslint-disable-next-line no-alert
      alert('Revenue trend data not available');
      return;
    }

    const trend = analytics.revenueTrend;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📈 30-Day Revenue Trend</h4>
          <div class="trend-summary-stats">
            <div class="trend-stat-card">
              <span class="trend-stat-label">Total Revenue</span>
              <span class="trend-stat-value">$${this.formatNumber(trend.totalRevenue)}</span>
            </div>
            <div class="trend-stat-card">
              <span class="trend-stat-label">Total Orders</span>
              <span class="trend-stat-value">${this.formatNumber(trend.totalOrders)}</span>
            </div>
            <div class="trend-stat-card">
              <span class="trend-stat-label">Avg Order Value</span>
              <span class="trend-stat-value">$${this.formatMoney(trend.avgOrderValue)}</span>
            </div>
            <div class="trend-stat-card">
              <span class="trend-stat-label">Date Range</span>
              <span class="trend-stat-value" style="font-size: 14px;">${trend.dateRange.start} to ${trend.dateRange.end}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📊 Daily Breakdown</h4>
          <div class="daily-trend-table">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: rgba(99, 102, 241, 0.2); border-bottom: 1px solid rgba(99, 102, 241, 0.3);">
                  <th style="padding: 12px; text-align: left;">Date</th>
                  <th style="padding: 12px; text-align: right;">Revenue</th>
                  <th style="padding: 12px; text-align: center;">Orders</th>
                  <th style="padding: 12px; text-align: right;">AOV</th>
                </tr>
              </thead>
              <tbody>
                ${trend.daily
                  .map((day) => {
                    const aov = day.orders > 0 ? (day.revenue / day.orders).toFixed(2) : '0.00';
                    return `
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                      <td style="padding: 10px; color: rgba(255,255,255,0.9);">${day.date}</td>
                      <td style="padding: 10px; text-align: right; color: #10b981; font-weight: 600;">$${this.formatMoney(day.revenue)}</td>
                      <td style="padding: 10px; text-align: center; color: rgba(255,255,255,0.7);">${day.orders}</td>
                      <td style="padding: 10px; text-align: right; color: rgba(255,255,255,0.6);">$${aov}</td>
                    </tr>
                  `;
                  })
                  .join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Channel Profitability modal
   */
  showChannelProfitability() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.channelProfitability) {
      // eslint-disable-next-line no-alert
      alert('Channel profitability data not available');
      return;
    }

    const channels = analytics.channelProfitability;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💸 Channel Profitability Analysis</h4>
          <p style="color: rgba(255,255,255,0.6); margin-top: 8px;">Net revenue after estimated channel fees (Shopify: 2.9%, Etsy: 6.5%, WooCommerce: 2.9%)</p>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: rgba(139, 92, 246, 0.2); border-bottom: 1px solid rgba(139, 92, 246, 0.3);">
                <th style="padding: 12px; text-align: left;">Channel</th>
                <th style="padding: 12px; text-align: center;">Orders</th>
                <th style="padding: 12px; text-align: right;">Gross Revenue</th>
                <th style="padding: 12px; text-align: right;">Fees</th>
                <th style="padding: 12px; text-align: right;">Net Revenue</th>
                <th style="padding: 12px; text-align: right;">AOV</th>
              </tr>
            </thead>
            <tbody>
              ${channels
                .map(
                  (ch) => `
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <td style="padding: 12px; color: rgba(255,255,255,0.9);"><strong>${this.getChannelIcon(ch.channel)} ${_esc(ch.channel)}</strong></td>
                  <td style="padding: 12px; text-align: center; color: rgba(255,255,255,0.7);">${ch.orders}</td>
                  <td style="padding: 12px; text-align: right; color: #3b82f6;">$${this.formatMoney(ch.revenue)}</td>
                  <td style="padding: 12px; text-align: right; color: #ef4444;">-$${this.formatMoney(ch.fees)}</td>
                  <td style="padding: 12px; text-align: right; color: #10b981; font-weight: 700;">$${this.formatMoney(ch.netRevenue)}</td>
                  <td style="padding: 12px; text-align: right; color: rgba(255,255,255,0.6);">$${this.formatMoney(ch.avgOrderValue)}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Fulfillment Efficiency modal
   */
  showFulfillmentDetails() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.fulfillmentEfficiency) {
      // eslint-disable-next-line no-alert
      alert('Fulfillment data not available');
      return;
    }

    const fulfillment = analytics.fulfillmentEfficiency;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>⏱️ Fulfillment Efficiency Dashboard</h4>
          <div class="fulfillment-summary-stats">
            <div class="fulfillment-stat-card">
              <span class="stat-label">Avg Days to Fulfill</span>
              <span class="stat-value" style="color: ${parseFloat(fulfillment.avgDays) > 7 ? '#ef4444' : parseFloat(fulfillment.avgDays) > 3 ? '#f59e0b' : '#10b981'}">${fulfillment.avgDays}</span>
            </div>
            <div class="fulfillment-stat-card alert">
              <span class="stat-label">Orders > 3 Days</span>
              <span class="stat-value">${this.formatNumber(fulfillment.ordersOver3Days)}</span>
            </div>
            <div class="fulfillment-stat-card alert">
              <span class="stat-label">Orders > 7 Days</span>
              <span class="stat-value">${this.formatNumber(fulfillment.ordersOver7Days)}</span>
            </div>
            <div class="fulfillment-stat-card alert">
              <span class="stat-label">Oldest Order</span>
              <span class="stat-value">${fulfillment.oldestOrderDays} days</span>
            </div>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📊 Order Aging Distribution</h4>
          <div class="aging-breakdown">
            ${Object.entries(fulfillment.agingBuckets)
              .map(([bucket, count]) => {
                const percentage =
                  fulfillment.totalPending > 0
                    ? ((count / fulfillment.totalPending) * 100).toFixed(1)
                    : 0;
                return `
                <div class="aging-bucket-item">
                  <div class="aging-bucket-header">
                    <span class="bucket-label">${bucket}</span>
                    <span class="bucket-count">${count} orders (${percentage}%)</span>
                  </div>
                  <div class="aging-bucket-bar">
                    <div class="aging-bucket-fill" style="width: ${percentage}%; background: ${bucket.includes('7+') ? '#ef4444' : bucket.includes('3-7') ? '#f59e0b' : '#10b981'};"></div>
                  </div>
                </div>
              `;
              })
              .join('')}
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Channel Processing Time modal
   */
  showChannelProcessingTime() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.processingTimeByChannel) {
      // eslint-disable-next-line no-alert
      alert('Channel processing time data not available');
      return;
    }

    const channels = analytics.processingTimeByChannel;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>⚡ Processing Time by Channel</h4>
          <p style="color: rgba(255,255,255,0.6); margin-top: 8px;">Average time from order placement to fulfillment by sales channel</p>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: rgba(99, 102, 241, 0.2); border-bottom: 1px solid rgba(99, 102, 241, 0.3);">
                <th style="padding: 12px; text-align: left;">Channel</th>
                <th style="padding: 12px; text-align: center;">Orders</th>
                <th style="padding: 12px; text-align: right;">Avg Days</th>
                <th style="padding: 12px; text-align: left;">Performance</th>
              </tr>
            </thead>
            <tbody>
              ${channels
                .map((ch) => {
                  const avgDays = parseFloat(ch.avgDays);
                  const performance =
                    avgDays <= 3
                      ? { label: '✅ Excellent', color: '#10b981' }
                      : avgDays <= 7
                        ? { label: '⚠️ Needs Improvement', color: '#f59e0b' }
                        : { label: '❌ Critical', color: '#ef4444' };
                  return `
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                    <td style="padding: 12px; color: rgba(255,255,255,0.9);"><strong>${this.getChannelIcon(ch.channel)} ${_esc(ch.channel)}</strong></td>
                    <td style="padding: 12px; text-align: center; color: rgba(255,255,255,0.7);">${ch.orders}</td>
                    <td style="padding: 12px; text-align: right; font-weight: 600; color: ${performance.color};">${ch.avgDays} days</td>
                    <td style="padding: 12px; color: ${performance.color};">${performance.label}</td>
                  </tr>
                `;
                })
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Order Volume Heatmap modal
   */
  showOrderHeatmap() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.orderVolumePatterns) {
      // eslint-disable-next-line no-alert
      alert('Order volume pattern data not available');
      return;
    }

    const patterns = analytics.orderVolumePatterns;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>🔥 Order Volume Heatmap</h4>
          <p style="color: rgba(255,255,255,0.6); margin-top: 8px;">Peak ordering times and patterns to optimize staffing and marketing</p>
        </div>

        <div class="detail-section">
          <h4>📅 Orders by Day of Week</h4>
          <div class="day-breakdown">
            ${(() => {
              // Hoist Math.max out of the map loop — was O(n²), now O(n).
              const dayValues = Object.values(patterns.byDayOfWeek);
              const maxDayOrders = dayValues.length > 0 ? Math.max(...dayValues) : 0;
              return Object.entries(patterns.byDayOfWeek)
                .map(([day, count]) => {
                  const percentage =
                    maxDayOrders > 0 ? ((count / maxDayOrders) * 100).toFixed(0) : 0;
                  return `
                <div class="day-item">
                  <span class="day-label">${day}</span>
                  <div class="day-bar-container">
                    <div class="day-bar-fill" style="width: ${percentage}%; background: #8b5cf6;"></div>
                  </div>
                  <span class="day-count">${count}</span>
                </div>
              `;
                })
                .join('');
            })()}
          </div>
        </div>

        <div class="detail-section">
          <h4>🕐 Orders by Hour</h4>
          <div class="hour-breakdown">
            ${(() => {
              // Hoist Math.max out of the map loop — was O(n²), now O(n).
              const hourValues = Object.values(patterns.byHour);
              const maxHourOrders = hourValues.length > 0 ? Math.max(...hourValues) : 0;
              return Object.entries(patterns.byHour)
                .map(([hour, count]) => {
                  const percentage =
                    maxHourOrders > 0 ? ((count / maxHourOrders) * 100).toFixed(0) : 0;
                  const hourLabel =
                    parseInt(hour, 10) === 0
                      ? '12 AM'
                      : parseInt(hour, 10) < 12
                        ? hour + ' AM'
                        : parseInt(hour, 10) === 12
                          ? '12 PM'
                          : parseInt(hour, 10) - 12 + ' PM';
                  return `
                  <div class="hour-item">
                    <span class="hour-label">${hourLabel}</span>
                    <div class="hour-bar-container">
                      <div class="hour-bar-fill" style="width: ${percentage}%; background: #06b6d4;"></div>
                    </div>
                    <span class="hour-count">${count}</span>
                  </div>
                `;
                })
                .join('');
            })()}
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>⭐ Peak Performance</h4>
          <div class="peak-stats">
            <div class="peak-stat-card">
              <span class="peak-label">Peak Day</span>
              <span class="peak-value">${patterns.peakDay?.day || '--'}</span>
              <span class="peak-count">${patterns.peakDay?.orders || 0} orders</span>
            </div>
            <div class="peak-stat-card">
              <span class="peak-label">Peak Hour</span>
              <span class="peak-value">${patterns.peakHour ? (patterns.peakHour.hour === 0 ? '12 AM' : patterns.peakHour.hour < 12 ? patterns.peakHour.hour + ' AM' : patterns.peakHour.hour === 12 ? '12 PM' : patterns.peakHour.hour - 12 + ' PM') : '--'}</span>
              <span class="peak-count">${patterns.peakHour?.orders || 0} orders</span>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Customer Retention modal
   */
  showCustomerRetention() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.customerRetention) {
      // eslint-disable-next-line no-alert
      alert('Customer retention data not available');
      return;
    }

    const retention = analytics.customerRetention;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>🔄 Customer Retention Analysis</h4>
          <div class="retention-summary-stats">
            <div class="retention-stat-card ${retention.repeatRate >= 30 ? 'success' : retention.repeatRate >= 20 ? 'warning' : 'alert'}">
              <span class="stat-label">Repeat Customer Rate</span>
              <span class="stat-value">${retention.repeatRate.toFixed(1)}%</span>
              <span class="stat-sublabel">${retention.repeatCustomers} of ${retention.uniqueCustomers} customers</span>
            </div>
            <div class="retention-stat-card">
              <span class="stat-label">Unique Customers</span>
              <span class="stat-value">${this.formatNumber(retention.uniqueCustomers)}</span>
            </div>
            <div class="retention-stat-card">
              <span class="stat-label">Repeat Customers</span>
              <span class="stat-value">${this.formatNumber(retention.repeatCustomers)}</span>
            </div>
            <div class="retention-stat-card">
              <span class="stat-label">Avg Orders/Customer</span>
              <span class="stat-value">${retention.avgOrdersPerCustomer}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💰 Revenue Split</h4>
          <div class="revenue-split-grid">
            <div class="revenue-split-card">
              <div class="split-icon" style="background: rgba(59, 130, 246, 0.2);">🆕</div>
              <div class="split-data">
                <span class="split-label">New Customer Revenue</span>
                <span class="split-value">$${this.formatNumber(retention.newCustomerRevenue)}</span>
                <span class="split-percent">${((parseFloat(retention.newCustomerRevenue) / (parseFloat(retention.newCustomerRevenue) + parseFloat(retention.repeatCustomerRevenue))) * 100).toFixed(1)}% of total</span>
              </div>
            </div>
            <div class="revenue-split-card">
              <div class="split-icon" style="background: rgba(34, 197, 94, 0.2);">🔄</div>
              <div class="split-data">
                <span class="split-label">Repeat Customer Revenue</span>
                <span class="split-value" style="color: #10b981;">$${this.formatNumber(retention.repeatCustomerRevenue)}</span>
                <span class="split-percent">${((parseFloat(retention.repeatCustomerRevenue) / (parseFloat(retention.newCustomerRevenue) + parseFloat(retention.repeatCustomerRevenue))) * 100).toFixed(1)}% of total</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💡 Insights</h4>
          <div class="insights-list">
            ${
              retention.repeatRate >= 30
                ? `
              <div class="insight-item success">
                <div class="insight-icon">✅</div>
                <div class="insight-content">
                  <strong>Excellent Retention:</strong> ${retention.repeatRate.toFixed(1)}% repeat rate exceeds industry average (20-30%). Focus on maintaining this success.
                </div>
              </div>
            `
                : retention.repeatRate >= 20
                  ? `
              <div class="insight-item info">
                <div class="insight-icon">📊</div>
                <div class="insight-content">
                  <strong>Average Retention:</strong> ${retention.repeatRate.toFixed(1)}% is within industry norms. Consider loyalty programs to increase.
                </div>
              </div>
            `
                  : `
              <div class="insight-item warning">
                <div class="insight-icon">⚠️</div>
                <div class="insight-content">
                  <strong>Low Retention:</strong> ${retention.repeatRate.toFixed(1)}% below industry average. Implement customer retention strategies.
                </div>
              </div>
            `
            }
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Show Concentration Risk modal
   */
  showConcentrationRisk() {
    const analytics = this.cache.summary?.analytics;
    if (!analytics || !analytics.concentrationRisk) {
      // eslint-disable-next-line no-alert
      alert('Concentration risk data not available');
      return;
    }

    const risk = analytics.concentrationRisk;
    const modalBody = document.getElementById('order-detail-body');

    _safeSetHTML(
      modalBody,
      `
      <div class="analytics-modal-grid">
        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>⚠️ Customer Concentration Risk Analysis</h4>
          <div class="risk-summary-card ${risk.risk === 'HIGH' ? 'alert' : risk.risk === 'MEDIUM' ? 'warning' : 'success'}">
            <div class="risk-level">
              <span class="risk-label">Risk Level:</span>
              <span class="risk-value" style="color: ${risk.risk === 'HIGH' ? '#ef4444' : risk.risk === 'MEDIUM' ? '#f59e0b' : '#10b981'};">
                ${risk.risk === 'HIGH' ? '🔴 HIGH RISK' : risk.risk === 'MEDIUM' ? '🟡 MEDIUM RISK' : '🟢 LOW RISK'}
              </span>
            </div>
            <p style="color: rgba(255,255,255,0.7); margin-top: 12px;">
              ${
                risk.risk === 'HIGH'
                  ? 'Warning: Heavy revenue dependency on top customer. Diversification recommended.'
                  : risk.risk === 'MEDIUM'
                    ? 'Moderate concentration detected. Monitor customer diversification.'
                    : 'Healthy revenue distribution across customer base.'
              }
            </p>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>👤 Top Customer Impact</h4>
          <div class="top-customer-card">
            <div class="customer-info">
              <span class="customer-name">${_esc(risk.topCustomer?.customer || '--')}</span>
              <div class="customer-stats">
                <span class="customer-revenue">$${this.formatMoney(risk.topCustomer?.revenue || 0)}</span>
                <span class="customer-percent" style="color: ${risk.topCustomerPercent > 20 ? '#ef4444' : risk.topCustomerPercent > 10 ? '#f59e0b' : '#10b981'};">
                  ${risk.topCustomerPercent.toFixed(1)}% of total revenue
                </span>
                <span class="customer-orders">${risk.topCustomer?.orders || 0} orders</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>📊 Top 10 Customers</h4>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: rgba(99, 102, 241, 0.2); border-bottom: 1px solid rgba(99, 102, 241, 0.3);">
                <th style="padding: 10px; text-align: left;">Rank</th>
                <th style="padding: 10px; text-align: left;">Customer</th>
                <th style="padding: 10px; text-align: center;">Orders</th>
                <th style="padding: 10px; text-align: right;">Revenue</th>
                <th style="padding: 10px; text-align: right;">% of Total</th>
              </tr>
            </thead>
            <tbody>
              ${risk.top10Customers
                .slice(0, 10)
                .map(
                  (customer, index) => `
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <td style="padding: 10px; color: rgba(255,255,255,0.6);">#${index + 1}</td>
                  <td style="padding: 10px; color: rgba(255,255,255,0.9);">${_esc(customer.customer)}</td>
                  <td style="padding: 10px; text-align: center; color: rgba(255,255,255,0.7);">${customer.orders}</td>
                  <td style="padding: 10px; text-align: right; color: #10b981;">$${this.formatMoney(customer.revenue)}</td>
                  <td style="padding: 10px; text-align: right; color: ${customer.percent > 20 ? '#ef4444' : customer.percent > 10 ? '#f59e0b' : 'rgba(255,255,255,0.6)'};">${customer.percent.toFixed(1)}%</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
            <tfoot>
              <tr style="background: rgba(139, 92, 246, 0.2); border-top: 2px solid rgba(139, 92, 246, 0.4);">
                <td colspan="3" style="padding: 12px; color: white; font-weight: 700;">Top 10 Total</td>
                <td style="padding: 12px; text-align: right; color: white; font-weight: 700;">--</td>
                <td style="padding: 12px; text-align: right; color: ${risk.top10Percent > 50 ? '#ef4444' : '#10b981'}; font-weight: 700;">${risk.top10Percent.toFixed(1)}%</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="detail-section" style="grid-column: 1 / -1;">
          <h4>💡 Recommendations</h4>
          <div class="insights-list">
            ${
              risk.topCustomerPercent > 20
                ? `
              <div class="insight-item warning">
                <div class="insight-icon">⚠️</div>
                <div class="insight-content">
                  <strong>High Dependency Risk:</strong> Top customer represents ${risk.topCustomerPercent.toFixed(1)}% of revenue. Develop strategies to diversify customer base and reduce dependency.
                </div>
              </div>
            `
                : ''
            }
            ${
              risk.top10Percent > 50
                ? `
              <div class="insight-item info">
                <div class="insight-icon">📊</div>
                <div class="insight-content">
                  <strong>Top-Heavy Distribution:</strong> Top 10 customers account for ${risk.top10Percent.toFixed(1)}% of revenue. Focus on customer acquisition and mid-tier growth.
                </div>
              </div>
            `
                : `
              <div class="insight-item success">
                <div class="insight-icon">✅</div>
                <div class="insight-content">
                  <strong>Balanced Distribution:</strong> Revenue is well-distributed across customer base (${risk.top10Percent.toFixed(1)}% from top 10). Continue diversification efforts.
                </div>
              </div>
            `
            }
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" data-action="close-modal">Close</button>
      </div>
    `
    );

    document.getElementById('order-detail-modal').style.display = 'flex';
    modalBody
      .querySelector('[data-action="close-modal"]')
      ?.addEventListener('click', () => this.closeModal());
  },

  /**
   * Close modal
   */
  closeModal() {
    document.getElementById('order-detail-modal').style.display = 'none';
  },

  /**
   * Get channel icon
   */
  getChannelIcon(channel) {
    const icons = {
      SHOPIFY: '🛍️',
      AMAZON: '📦',
      EBAY: '🔨',
      ETSY: '🎨',
      DIRECT: '🏪',
      WALMART: '🏬',
    };
    return icons[channel?.toUpperCase()] || '🌐';
  },

  formatDate(dateString) {
    if (!dateString || dateString === 'N/A') return 'N/A';

    try {
      const date = window.AdminDashboardCore.safeDateParse(dateString);
      if (!date) return dateString;
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      if (isDev) console.warn('Date parsing failed:', dateString, error);
      return dateString;
    }
  },

  /**
   * Show error message
   */
  showError(message) {
    const elements = [
      'linnworks-total-orders',
      'linnworks-pending-count',
      'linnworks-channel-breakdown',
      'linnworks-inventory-stats',
    ];

    elements.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        _safeSetHTML(el, `<span class="error-message">⚠️ ${message}</span>`);
      }
    });
  },

  /**
   * Format number with commas — delegates to core.formatNumber for locale-aware
   * thousands separators and consistent decimal handling across all modules.
   * Accepts a pre-formatted string (e.g. "1234.56" from .toFixed()) or a raw number.
   */
  formatNumber(num) {
    if (num === null || num === undefined) return '0';
    if (window.AdminDashboardCore?.formatNumber) {
      return window.AdminDashboardCore.formatNumber(num);
    }
    // Fallback: simple comma insertion (used only if core is not yet loaded)
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  /**
   * Format amount as a fixed-decimal string — delegates to core.formatNumber(amount, 2).
   * Returns the numeric portion only (no '$' prefix) for callers that prepend the symbol.
   */
  formatMoney(amount) {
    if (amount === null || amount === undefined) return '0.00';
    if (window.AdminDashboardCore?.formatNumber) {
      return window.AdminDashboardCore.formatNumber(amount, 2);
    }
    // Fallback if core not yet loaded
    return parseFloat(amount).toFixed(2);
  },

  // Days elapsed from order date to now (returns 0 on parse failure, safe fallback).
  getDaysOld(dateString) {
    if (!dateString || dateString === 'N/A') return 0;

    try {
      const orderDate = window.AdminDashboardCore.safeDateParse(dateString);
      if (!orderDate) return 0;
      const now = new Date();
      const diffTime = Math.abs(now - orderDate);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      if (isDev) console.warn('Date calculation failed:', dateString, error);
      return 0;
    }
  },

  /**
   * Load tracking audit data
   */
  // 7d default (not 30d) because order aging at fulfillment centers is weekly. 30d obscures same-week SLA failures.
  async loadTrackingData(timePeriod = '7d') {
    this._transitionSyncState('syncing', 'loadTrackingData', { timePeriod });
    const t0 = Date.now();
    try {
      // Show loading indicator
      const loadingIndicator = document.getElementById('tracking-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.classList.add('show');
        loadingIndicator.style.display = 'flex';
      }

      const response = await fetch(
        `${this.apiUrl}?endpoint=tracking-audit&timePeriod=${timePeriod}`
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        if (isDev) console.warn('[Linnworks] loadTrackingData: server returned error', data.error);
        this._transitionSyncState('error', 'loadTrackingData', { ms: Date.now() - t0 });
        return;
      }

      // Warn in dev mode if tracking data shape is unexpected
      if (isDev) {
        if (!Array.isArray(data.labelsPrinted)) {
          console.warn(
            '[Linnworks] loadTrackingData: data.labelsPrinted not an array',
            typeof data.labelsPrinted
          );
        }
        if (!Array.isArray(data.processedOrders)) {
          console.warn(
            '[Linnworks] loadTrackingData: data.processedOrders not an array',
            typeof data.processedOrders
          );
        }
      }

      this.updateTrackingUI(data);

      // Update last updated time
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const lastUpdatedEl = document.getElementById('tracking-last-updated');
      if (lastUpdatedEl) {
        lastUpdatedEl.textContent = timeString;
      }

      this._transitionSyncState('complete', 'loadTrackingData', { ms: Date.now() - t0 });
    } catch (_error) {
      // non-critical: tracking refresh failure does not block main dashboard
      if (isDev) console.warn('[Linnworks] loadTrackingData failed:', _error);
      this._transitionSyncState('error', 'loadTrackingData', { ms: Date.now() - t0 });
    } finally {
      const loadingIndicator = document.getElementById('tracking-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.classList.remove('show');
        loadingIndicator.style.display = 'none';
      }
    }
  },

  // Updates stat cards, color-codes stuck orders (>5 = warning, >10 = critical),
  // renders 4 tracking tables, and auto-switches to Labels tab if stuck queue is empty.
  updateTrackingUI(data) {
    const labelsPrintedCount = document.getElementById('labels-printed-count');
    const processedCount = document.getElementById('processed-count');
    const stuckCount = document.getElementById('stuck-orders-count');
    const shippedCount = document.getElementById('shipped-count');

    if (labelsPrintedCount) {
      labelsPrintedCount.textContent = this.formatNumber(data.labelsPrinted?.length || 0);
    }

    if (processedCount) {
      processedCount.textContent = this.formatNumber(data.processedOrders?.length || 0);
    }

    if (stuckCount) {
      stuckCount.textContent = this.formatNumber(data.stuckOrders?.length || 0);
    }

    if (shippedCount) {
      shippedCount.textContent = this.formatNumber(data.shippedOrders?.length || 0);
    }

    // Color-code stuck orders: 5 = threshold for warning, 10 = critical
    const stuckCard = document.getElementById('stuck-orders-card');
    if (stuckCard) {
      stuckCard.classList.remove('critical', 'warning');
      const count = data.stuckOrders?.length || 0;
      if (count > 10) {
        stuckCard.classList.add('critical');
      } else if (count > 5) {
        stuckCard.classList.add('warning');
      }
    }

    this.renderStuckOrdersTable(data.stuckOrders || []);
    this.renderLabelsPrintedTable(data.labelsPrinted || []);
    this.renderProcessedOrdersTable(data.processedOrders || []);
    this.renderShippedOrdersTable(data.shippedOrders || []);

    // Auto-switch to Labels tab when stuck queue clears (UX: show progress, reduce operator click).
    const stuckOrdersCount = data.stuckOrders?.length || 0;
    if (stuckOrdersCount === 0) {
      this.switchTrackingView('labels');
    }
  },

  /**
   * Generate tracking URL for carrier
   */
  getTrackingUrl(trackingNumber, carrier) {
    if (!trackingNumber) return '#';

    const carrierUpper = (carrier || '').toUpperCase();

    if (carrierUpper.includes('USPS')) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    } else if (carrierUpper.includes('UPS')) {
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    } else if (carrierUpper.includes('FEDEX')) {
      return `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
    }

    // Default: try USPS
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  },

  /**
   * Render stuck orders table (MOST IMPORTANT)
   */
  renderStuckOrdersTable(orders) {
    const tableDiv = document.getElementById('stuck-orders-table');
    if (!tableDiv) return;

    if (!orders || orders.length === 0) {
      _safeSetHTML(
        tableDiv,
        '<p style="text-align: center; color: rgba(255,255,255,0.6); padding: 40px;">✅ No stuck orders - all clear!</p>'
      );
      return;
    }

    // Sort by days old DESC (oldest first)
    const sorted = orders.sort((a, b) => b.daysOld - a.daysOld);

    const html = `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Status</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Order #</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Customer</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Days Stuck</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Channel</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Tracking</th>
          </tr>
        </thead>
        <tbody>
          ${sorted
            .map((order) => {
              const statusIcon = order.daysOld > 3 ? '🔴' : order.daysOld > 1 ? '🟡' : '🟢';
              const bgColor =
                order.daysOld > 3
                  ? 'rgba(239,68,68,0.1)'
                  : order.daysOld > 1
                    ? 'rgba(245,158,11,0.1)'
                    : 'rgba(34,197,94,0.05)';
              const borderColor =
                order.daysOld > 3
                  ? this.STATUS_COLORS.danger
                  : order.daysOld > 1
                    ? this.STATUS_COLORS.warning
                    : 'transparent';

              return `
              <tr style="background: ${bgColor}; border-left: 3px solid ${borderColor};">
                <td style="padding: 12px; font-size: 20px;">${statusIcon}</td>
                <td style="padding: 12px;">
                  <a href="#" data-order-detail="${_esc(order.orderId)}" style="color: ${this.INTERACTION_COLORS.link}; font-weight: 600; text-decoration: none;">
                    ${_esc(order.orderNumber)}
                  </a>
                </td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${_esc(order.customer)}</td>
                <td style="padding: 12px; color: white; font-weight: 600;">${order.daysOld} ${order.daysOld === 1 ? 'day' : 'days'}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6); font-size: 12px;">${_esc(order.trackingNumber || '-')}</td>
              </tr>
            `;
            })
            .join('')}
        </tbody>
      </table>
    `;

    _safeSetHTML(tableDiv, html);
    _bindOrderDetailDelegation(tableDiv, this);
  },

  /**
   * Render labels printed table
   */
  renderLabelsPrintedTable(orders) {
    const tableDiv = document.getElementById('labels-printed-table');
    if (!tableDiv) return;

    if (!orders || orders.length === 0) {
      _safeSetHTML(
        tableDiv,
        '<p style="text-align: center; color: rgba(255,255,255,0.6); padding: 40px;">No labels printed yet</p>'
      );
      return;
    }

    // Sort by label printed date descending (most recent first)
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = a.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(a.labelPrintedDate)
        : new Date(0);
      const dateB = b.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(b.labelPrintedDate)
        : new Date(0);
      return (dateB || new Date(0)) - (dateA || new Date(0));
    });

    const html = `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Order #</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Customer</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Order Date</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Label Date</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Ship Date</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Channel</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Tracking</th>
          </tr>
        </thead>
        <tbody>
          ${sortedOrders
            .map((order) => {
              const trackingUrl = order.trackingNumber
                ? this.getTrackingUrl(order.trackingNumber, order.carrier)
                : '#';
              const trackingHtml = order.trackingNumber
                ? `<a href="${_esc(trackingUrl)}" target="_blank" style="color: ${this.INTERACTION_COLORS.link}; text-decoration: none; font-size: 12px;">${_esc(order.trackingNumber)}</a>`
                : '<span style="color: rgba(255,255,255,0.4); font-size: 12px;">-</span>';

              const orderDateObj = window.AdminDashboardCore.safeDateParse(order.receivedDate);
              const orderDate = orderDateObj
                ? orderDateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '-';

              // Label date (from ProcessedOrders API merged data)
              const labelDateObj = window.AdminDashboardCore.safeDateParse(order.labelPrintedDate);
              const labelDate = labelDateObj
                ? labelDateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '<span style="color: rgba(156,163,175,0.6);">-</span>';

              // Determine ship date/status to display
              let shipDate;
              if (order.labelPrintedDate) {
                // Has actual ship date from processed orders
                const shipDateObj = window.AdminDashboardCore.safeDateParse(order.labelPrintedDate);
                shipDate = shipDateObj
                  ? shipDateObj.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '-';
              } else if (order.trackingStatus) {
                // Has tracking status from carrier (e.g., "Pre-Shipment", "In Transit", "Delivered")
                const statusColors = {
                  'pre-shipment': 'rgba(245,158,11,0.9)',
                  'in transit': 'rgba(59,130,246,0.9)',
                  'out for delivery': 'rgba(139,92,246,0.9)',
                  delivered: 'rgba(34,197,94,0.9)',
                  exception: 'rgba(239,68,68,0.9)',
                };
                const statusLower = order.trackingStatus.toLowerCase();
                const color = statusColors[statusLower] || 'rgba(156,163,175,0.9)';
                shipDate = `<span style="color: ${color}; font-weight: 500;">${_esc(order.trackingStatus)}</span>`;
              } else if (order.trackingNumber) {
                // Has tracking but no status
                shipDate = '<span style="color: rgba(34,197,94,0.8);">Label Created</span>';
              } else if (order.labelPrinted) {
                // Label printed but no tracking
                shipDate = '<span style="color: rgba(245,158,11,0.8);">Label Printed</span>';
              } else {
                // Nothing yet
                shipDate = '<span style="color: rgba(156,163,175,0.6);">Not Shipped</span>';
              }

              return `
              <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                <td style="padding: 12px;">
                  <a href="#" data-order-detail="${_esc(order.orderId)}" style="color: ${this.INTERACTION_COLORS.link}; font-weight: 600; text-decoration: none;">
                    ${_esc(order.orderNumber)}
                  </a>
                </td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${_esc(order.customer)}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6); font-size: 13px;">${orderDate}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6); font-size: 13px;">${labelDate}</td>
                <td style="padding: 12px; font-size: 13px;">${shipDate}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</td>
                <td style="padding: 12px;">${trackingHtml}</td>
              </tr>
            `;
            })
            .join('')}
        </tbody>
      </table>
    `;

    _safeSetHTML(tableDiv, html);
    _bindOrderDetailDelegation(tableDiv, this);
  },

  /**
   * Render processed orders table
   */
  renderProcessedOrdersTable(orders) {
    const tableDiv = document.getElementById('processed-orders-table');
    if (!tableDiv) return;

    if (!orders || orders.length === 0) {
      _safeSetHTML(
        tableDiv,
        '<p style="text-align: center; color: rgba(255,255,255,0.6); padding: 40px;">No orders ready to ship</p>'
      );
      return;
    }

    // Sort by label printed date descending (most recent first)
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = a.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(a.labelPrintedDate)
        : new Date(0);
      const dateB = b.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(b.labelPrintedDate)
        : new Date(0);
      return (dateB || new Date(0)) - (dateA || new Date(0));
    });

    const html = `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Order #</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Customer</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Processed Date</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Channel</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${sortedOrders
            .map((order) => {
              const processedDateObj = window.AdminDashboardCore.safeDateParse(
                order.labelPrintedDate
              );
              const processedDate = processedDateObj
                ? processedDateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '<span style="color: rgba(156,163,175,0.6);">-</span>';
              return `
              <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                <td style="padding: 12px;">
                  <a href="#" data-order-detail="${_esc(order.orderId)}" style="color: ${this.INTERACTION_COLORS.link}; font-weight: 600; text-decoration: none;">
                    ${_esc(order.orderNumber)}
                  </a>
                </td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${_esc(order.customer)}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6);">${processedDate}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${this.getChannelIcon(order.channel)} ${_esc(order.channel)}</td>
                <td style="padding: 12px; color: #10b981; font-weight: 600;">${this.formatMoney(order.total)}</td>
              </tr>
            `;
            })
            .join('')}
        </tbody>
      </table>
    `;

    _safeSetHTML(tableDiv, html);
    _bindOrderDetailDelegation(tableDiv, this);
  },

  /**
   * Render shipped orders table
   */
  renderShippedOrdersTable(orders) {
    const tableDiv = document.getElementById('shipped-orders-table');
    if (!tableDiv) return;

    if (!orders || orders.length === 0) {
      _safeSetHTML(
        tableDiv,
        '<p style="text-align: center; color: rgba(255,255,255,0.6); padding: 40px;">No shipped orders yet</p>'
      );
      return;
    }

    // Sort by label printed date descending (most recent first)
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = a.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(a.labelPrintedDate)
        : new Date(0);
      const dateB = b.labelPrintedDate
        ? window.AdminDashboardCore.safeDateParse(b.labelPrintedDate)
        : new Date(0);
      return (dateB || new Date(0)) - (dateA || new Date(0));
    });

    const html = `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Order #</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Customer</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Label Date</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Carrier</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Tracking Number</th>
            <th style="padding: 12px; text-align: left; color: rgba(255,255,255,0.6); font-size: 12px; font-weight: 600;">Ship Date</th>
          </tr>
        </thead>
        <tbody>
          ${sortedOrders
            .map((order) => {
              const labelDateObj = window.AdminDashboardCore.safeDateParse(order.labelPrintedDate);
              const labelDate = labelDateObj
                ? labelDateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '<span style="color: rgba(156,163,175,0.6);">-</span>';

              const shipDateObj = window.AdminDashboardCore.safeDateParse(order.labelPrintedDate);
              const shipDate = shipDateObj
                ? shipDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '-';

              // Use helper method for tracking URL
              const trackingUrl = this.getTrackingUrl(order.trackingNumber, order.carrier);

              return `
              <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                <td style="padding: 12px;">
                  <a href="#" data-order-detail="${_esc(order.orderId)}" style="color: ${this.INTERACTION_COLORS.link}; font-weight: 600; text-decoration: none;">
                    ${_esc(order.orderNumber)}
                  </a>
                </td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${_esc(order.customer)}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6); font-size: 13px;">${labelDate}</td>
                <td style="padding: 12px; color: rgba(255,255,255,0.8);">${_esc(order.carrier || 'Unknown')}</td>
                <td style="padding: 12px;">
                  <a href="${_esc(trackingUrl)}" target="_blank" style="color: ${this.INTERACTION_COLORS.link}; text-decoration: none; font-size: 12px;">
                    ${_esc(order.trackingNumber)}
                  </a>
                </td>
                <td style="padding: 12px; color: rgba(255,255,255,0.6);">${shipDate}</td>
              </tr>
            `;
            })
            .join('')}
        </tbody>
      </table>
    `;

    _safeSetHTML(tableDiv, html);
    _bindOrderDetailDelegation(tableDiv, this);
  },

  // Tab state machine: hide all, clear all active styling, show selected, highlight tab.
  switchTrackingView(view) {
    document.querySelectorAll('.tracking-table-view').forEach((el) => {
      el.style.display = 'none';
    });

    document.querySelectorAll('.tracking-tab').forEach((tab) => {
      tab.classList.remove('active');
      tab.style.color = 'rgba(255,255,255,0.6)';
      tab.style.borderBottom = '2px solid transparent';
    });

    const viewMap = {
      stuck: 'stuck-orders-table',
      labels: 'labels-printed-table',
      processed: 'processed-orders-table',
      shipped: 'shipped-orders-table',
    };

    const tableId = viewMap[view];
    const tableEl = document.getElementById(tableId);
    if (tableEl) {
      tableEl.style.display = 'block';
    }

    const activeTab = document.querySelector(`.tracking-tab[data-view="${view}"]`);
    if (activeTab) {
      activeTab.classList.add('active');
      // Apply active tab styling
      activeTab.style.color = 'white';
      activeTab.style.borderBottom = `2px solid ${this.INTERACTION_COLORS.link}`;
    }
  },
};

// Expose on window so non-module scripts can access AdminDashboardLinnworks
window.AdminDashboardLinnworks = AdminDashboardLinnworks;

// Auto-initialize when dashboard loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Wait for main dashboard to initialize first
    setTimeout(
      () => AdminDashboardLinnworks.init(),
      AdminDashboardLinnworks.REFRESH_INTERVALS.initDelay
    );
  });
} else {
  setTimeout(() => AdminDashboardLinnworks.init(), 1000);
}
